// src/contexts/AuthContext.tsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

import { AccountDto } from '@playball/common/dto/Account'

// Define the User type based on your backend's Account entity
// Ensure this matches the structure returned by your /api/auth/me endpoint
export interface User {
  id: number;
  nickname: string;
  email: string;
  reward_points?: number;
  created_at?: string; // Or Date, depending on how it's serialized
  // Add any other fields your frontend needs from the Account entity
}

// Define the shape of your AuthContext
interface AuthContextType {
  user: AccountDto | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  checkAuthStatus: () => Promise<void>;
  initiateLogin: (provider: 'naver' | 'kakao' | 'google' | 'apple' , inviteCode?: string | null) => void;
  loginWithToken: (provider: 'naver' | 'kakao', token: string, inviteCode?: string | null) => void;
  logout: () => Promise<void>;
  performSignup: (signupData: {
    nickname: string;
    email: string; // Usually from OAuth provider
    // You'll need to pass the encrypted token or provider details for backend signup
    // For simplicity, let's assume the backend /api/signup expects these directly
    // after OAuth has pre-filled some info and redirected to React's signup page.
    // The backend's /api/signup would then call req.login().
    // Alternatively, the backend signup could directly create the session
    // and this function would just call checkAuthStatus().
    // Based on previous discussion, backend signup calls req.login().
    // So, this function will post to /api/signup, and then we'll refresh auth.
    token?: string; // If you were passing an encrypted token from backend to React signup
    inviteLeague?: string | null;
    provider?: string; // If passing provider details directly
    providerId?: string; // If passing provider details directly
  }) => Promise<AccountDto | null>; // Returns user if signup leads to login
  fetchApi: (endpoint: string, options?: RequestInit) => Promise<any>; // API utility
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define constants for backend URLs (should be in .env for a real app)
const BACKEND_URL = (process.env.REACT_APP_BACKEND_API_URL ?? 'http://localhost:3000').trim(); // Adjust to your backend port


// API Utility function (can be in a separate services/api.ts file)
async function fetchWithCredentials(endpoint: string, options: RequestInit = {}): Promise<any> {
  
  const defaultOptions: RequestInit = {
    credentials: 'include', // Crucial for sending/receiving session cookies
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  
  const response = await fetch(`${BACKEND_URL}${endpoint}`, defaultOptions);

  if (!response.ok) {
    if (response.status === 401) {
      // Unauthorized - could trigger a global logout or redirect if desired
      console.warn(`API call to ${endpoint} unauthorized.`);

      
      // For a fetchApi used within AuthContext, throwing an error is usually enough
      // as the context methods will handle it.
    }
    // Try to parse error JSON, otherwise use statusText
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `API Error: ${response.status}`);
  }

  if (response.status === 204) { // No Content
    return null;
  }

  return response.json().catch(() => ({ message: response.statusText }));
}


// Create the AuthProvider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AccountDto | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start loading until first auth check

  const fetchApi = useCallback(fetchWithCredentials, []);

  const checkAuthStatus = useCallback(async () => {
    if (!isLoading) setIsLoading(true);
    try {
      const userData = await fetchApi('/api/auth/me'); // Your backend endpoint
      setUser(userData as AccountDto);
    } catch (error) {
        console.info('User not authenticated or error fetching status:', error);
      setUser(null); // Ensure user is null if fetch fails (e.g., 401)
    } finally {
      setIsLoading(false);
    }
  }, [fetchApi, isLoading]); // Added isLoading to dependencies

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on initial mount

  const initiateLogin = (provider: 'naver' | 'kakao' | 'google' | 'apple' , inviteCode?: string | null) => {
    const frontendUrl = window.location.origin; 
    const frontendCallbackUrl = `${frontendUrl}/auth/callback`

    const authUrl = new URL(`${BACKEND_URL}/api/oauth/${provider}`, window.location.origin);

    authUrl.searchParams.append('frontend_redirect_uri', frontendCallbackUrl);
    if (inviteCode) {
        authUrl.searchParams.append('invite_league', inviteCode);
    }

    // 완성된 URL로 브라우저를 이동시켜 백엔드 인증 절차를 시작합니다.
    window.location.replace(authUrl.toString());
  };

  const loginWithToken = (provider: 'naver' | 'kakao', token: string, inviteCode?: string | null) => {
    const frontendUrl = window.location.origin; 
    const frontendCallbackUrl = `${frontendUrl}/auth/callback`

    const authUrl = new URL(`${BACKEND_URL}/api/oauth/${provider}/callback`, window.location.origin);
    authUrl.searchParams.append('callback_uri', `${window.location.origin}${window.location.pathname}`);
    authUrl.searchParams.append('token', token);
    authUrl.searchParams.append('frontend_redirect_uri', frontendCallbackUrl);
    if (inviteCode) {
        authUrl.searchParams.append('invite_league', inviteCode);
    }

    // 완성된 URL로 브라우저를 이동시켜 백엔드 인증 절차를 시작합니다.
    window.location.replace(authUrl.toString());
  };

  const performSignup = async (signupData: { nickname: string; email: string; token?: string; inviteLeague?: string | null; provider?: string; providerId?: string;}) => {
    setIsLoading(true);
    try {
      // The backend's /api/signup should handle creating the user
      // AND calling req.login() to establish a session.
      await fetchApi('/api/signup', {
        method: 'POST',
        body: JSON.stringify(signupData),
      });
      // After successful signup & session creation on backend, refresh React's auth state
      await checkAuthStatus();
      // The 'user' state will be updated by checkAuthStatus
      // We return the user state from the context which should now be populated.
      // A small delay might be needed if checkAuthStatus is not immediately reflecting.
      // However, checkAuthStatus sets isLoading to false, so components can react to that.
      return user; // This will be the user state *after* checkAuthStatus completes.
    } catch (error) {
      console.error("Signup failed:", error);
      setUser(null); // Ensure user is null on signup failure
      setIsLoading(false);
      throw error; // Re-throw for the component to handle (e.g., display error message)
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await fetchApi('/api/logout', { method: 'GET' }); // Or POST, match your backend
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Still set user to null on the frontend even if backend call fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user && !isLoading, // Only authenticated if not loading and user exists
        checkAuthStatus,
        initiateLogin,
        loginWithToken,
        logout,
        performSignup,
        fetchApi, // Expose fetchApi if components need to make authenticated calls
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Create the useAuth custom hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
